/**
 * Opening the record behind an Actions-tab card's Open Email / Open Document
 * button (see inAppLinks.ts). These leave the task pane — the message opens in
 * Outlook, the document in Office for the web — so TaskPane runs them directly
 * instead of switching tab.
 */
import type { RefreshTokenFn } from "./api/fetchWithRefresh";
import { emitToast } from "../../integrations/tools/outlook/toast-bridge";
import { isWebUrl, resolveDriveItem, toBrowserViewUrl } from "../integrations/files/services/driveItemRef";
import { graphGetJson } from "../integrations/email/services/graph";
import { logConnectorFailure, logOfficeError } from "../telemetry";

export const CONNECT_M365_TOAST = "Connect Microsoft 365 in Settings to open this document.";
export const DOCUMENT_OPEN_FAILED_TOAST = "Couldn't open this document. It may have moved, or you may not have access.";

export const EMAIL_OPEN_FAILED_TOAST = "Couldn't open this email. It may have moved, or you may not have access.";
export const CONNECT_M365_EMAIL_TOAST = "Connect Microsoft 365 in Settings to open this email.";

type OpenMessage = (itemId: string, webLink?: string) => void;

/**
 * Open the message `ref` names (a Graph REST id, which is what skills receive
 * from Graph-backed triggers). displayMessageForm where the host has it;
 * elsewhere (Word, New Outlook) the Outlook-on-the-web fallback MS-01 pairs
 * with it. That link is the message's own Graph `webLink` rather than one
 * built here, because the Outlook host differs per tenant and cloud
 * (outlook.office.com, outlook.office365.us, …) and only Graph knows it.
 */
export async function openEmailRecord(
  ref: string,
  openMessage: OpenMessage,
  toEwsId: (id: string) => string,
  graphToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<void> {
  const id = ref.trim();
  if (typeof Office !== "undefined" && typeof Office.context?.mailbox?.displayMessageForm === "function") {
    openMessage(toEwsId(id));
    return;
  }
  if (!graphToken) {
    emitToast({ title: CONNECT_M365_EMAIL_TOAST, intent: "error" });
    return;
  }
  try {
    const message = await graphGetJson<{ webLink?: unknown }>(
      `/me/messages/${encodeURIComponent(id)}?$select=webLink`,
      graphToken,
      refresh,
    );
    if (typeof message?.webLink !== "string" || !message.webLink) throw new Error("Message has no webLink");
    openMessage(id, message.webLink);
  } catch (err) {
    logConnectorFailure("outlook", err, "open-email-record");
    emitToast({ title: EMAIL_OPEN_FAILED_TOAST, intent: "error" });
  }
}

/**
 * Open `url` outside the pane. openBrowserWindow first: the call runs after an
 * awaited Graph lookup, outside the click's user gesture, where a browser host
 * may block window.open as a popup.
 */
export function openInBrowser(url: string): void {
  try {
    if (typeof Office !== "undefined" && typeof Office.context?.ui?.openBrowserWindow === "function") {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch (err) {
    logOfficeError(err, "in-app-record-open-browser-window");
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Open the document `ref` names. A web URL opens as-is; an item id is first
 * resolved to its webUrl through Graph. Every failure ends in a toast.
 */
export async function openDocumentRecord(
  ref: string,
  graphToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<void> {
  if (isWebUrl(ref)) {
    openInBrowser(toBrowserViewUrl(ref.trim()));
    return;
  }
  if (!graphToken) {
    emitToast({ title: CONNECT_M365_TOAST, intent: "error" });
    return;
  }
  try {
    const item = await resolveDriveItem(graphToken, ref, refresh);
    openInBrowser(toBrowserViewUrl(item.webUrl));
  } catch (err) {
    logConnectorFailure("onedrive", err, "open-document-record");
    emitToast({ title: DOCUMENT_OPEN_FAILED_TOAST, intent: "error" });
  }
}
